<template>
  <div class="account-view">
    <v-container>
      <h1 class="page-title mb-6">Account Settings</h1>

      <v-tabs v-model="activeTab" background-color="transparent" grow class="mb-6">
        <v-tab :value="0">Profile</v-tab>
        <v-tab :value="1">Expertise</v-tab>
        <v-tab :value="2">Password</v-tab>
        <v-tab :value="3">Notifications</v-tab>
      </v-tabs>

      <v-window v-model="activeTab">
        <!-- Profile Tab -->
        <v-window-item :value="0">
          <!-- General error messages for profile updates -->
          <div v-if="formErrors.non_field_errors || formErrors.detail" class="error-message-section mb-4">
            <div class="error-message-box">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <span>{{ formErrors.non_field_errors || formErrors.detail }}</span>
            </div>
          </div>
          
          <form-section title="Personal Information">
            <!-- Profile Picture Section -->
            <v-row class="mb-4">
              <v-col cols="12">
                <div class="profile-picture-section">
                  <label class="form-label">Profile Picture</label>
                  <div class="profile-picture-container">
                    <div class="current-picture">
                      <img 
                        v-if="profilePicturePreview || profile.profilePictureUrl" 
                        :src="profilePicturePreview || profile.profilePictureUrl" 
                        alt="Profile Picture"
                        class="profile-picture-img"
                      />
                      <div v-else class="profile-picture-placeholder">{{ profileInitials }}</div>
                    </div>
                    <div class="profile-picture-actions">
                      <input
                        ref="profilePictureInput"
                        type="file"
                        accept="image/*"
                        @change="handleProfilePictureChange"
                        style="display: none"
                      />
                      <action-button
                        variant="secondary"
                        size="small"
                        @click="$refs.profilePictureInput.click()"
                        left-icon="mdi-camera"
                      >
                        {{ profile.profilePictureUrl ? 'Change Picture' : 'Upload Picture' }}
                      </action-button>
                      <action-button
                        v-if="profile.profilePictureUrl || profilePicturePreview"
                        variant="outline"
                        size="small"
                        @click="removeProfilePicture"
                        left-icon="mdi-delete"
                        class="ml-2"
                      >
                        Remove
                      </action-button>
                    </div>
                  </div>
                  <div v-if="formErrors.profile_picture" class="error-message">{{ formErrors.profile_picture }}</div>
                  <small class="form-helper-text">
                    Recommended: Square image, at least 200x200 pixels. Max file size: 5MB.
                  </small>
                </div>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <base-input
                  id="firstName"
                  label="First Name"
                  v-model="profile.firstName"
                  placeholder="Your first name"
                  required
                  :error="formErrors.first_name"
                />
              </v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="lastName"
                  label="Last Name"
                  v-model="profile.lastName"
                  placeholder="Your last name"
                  required
                  :error="formErrors.last_name"
                />
              </v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="email"
                  label="Email"
                  v-model="profile.email"
                  placeholder="Your email address"
                  type="email"
                  disabled
                  :error="formErrors.email"
                />
              </v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="mobilePhone"
                  label="Mobile Phone"
                  v-model="profile.mobilePhone"
                  placeholder="Your mobile phone number"
                  :error="formErrors.mobile_phone"
                />
              </v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="linkedinProfileUrl"
                  label="LinkedIn Profile URL"
                  v-model="profile.linkedinProfileUrl"
                  placeholder="https://linkedin.com/in/your-profile"
                  :error="formErrors.linkedin_profile_url"
                />
              </v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="currentCompany"
                  label="Company you work for"
                  v-model="profile.currentCompany"
                  placeholder="Company name"
                  required
                  :error="formErrors.current_company"
                />
              </v-col>
              <v-col cols="12" md="6">
                <div class="form-group">
                  <label for="birthdate">Birthdate</label>
                  <v-menu
                    v-model="birthdateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    min-width="290px"
                  >
                    <template #activator="props">
                      <input
                        id="birthdate"
                        v-bind="props"
                        v-model="profile.birthdate"
                        type="date"
                        class="form-control"
                        :class="{ 'error': formErrors.birthdate }"
                        placeholder="YYYY-MM-DD"
                      />
                    </template>
                    <v-date-picker
                      v-model="profile.birthdate"
                      @input="birthdateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <div v-if="formErrors.birthdate" class="error-message">{{ formErrors.birthdate }}</div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <radio-group
                  name="gender"
                  label="Gender"
                  v-model="profile.gender"
                  :options="genderOptions"
                  :error="formErrors.gender"
                />
              </v-col>
              <v-col cols="12">
                <div class="form-group">
                  <label for="expertiseLevel">Level of Expertise</label>
                  <select 
                    id="expertiseLevel"
                    v-model="profile.expertiseLevel"
                    class="form-control"
                    :class="{ 'error': formErrors.expertise_level }"
                  >
                    <option v-for="option in expertiseLevelOptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                    </option>
                  </select>
                  <div v-if="formErrors.expertise_level" class="error-message">{{ formErrors.expertise_level }}</div>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="form-group">
                  <label for="specialization">Specialization</label>
                  <textarea
                    id="specialization"
                    v-model="profile.specialization"
                    class="form-control"
                    :class="{ 'error': formErrors.specialization }"
                    rows="3"
                    placeholder="Your area of specialization"
                  ></textarea>
                  <div v-if="formErrors.specialization" class="error-message">{{ formErrors.specialization }}</div>
                </div>
              </v-col>
            </v-row>
          </form-section>
        </v-window-item>

        <!-- Expertise Tab -->
        <v-window-item :value="1">
          <!-- General error messages for expertise updates -->
          <div v-if="formErrors.expertiseAreas || formErrors.industries || formErrors.geographicExpertise || formErrors.ageExpertise || formErrors.backgroundExpertise" class="error-message-section mb-4">
            <div class="error-message-box">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <span>Please complete all required fields in your expertise profile</span>
            </div>
          </div>
          
          <ExpertiseForm
            ref="expertiseForm"
            :selectedBrands="[]"
            :otherBrandExperience="''"
            :selectedExpertiseAreas="selectedExpertiseAreas"
            :otherExpertise="otherExpertise"
            :selectedIndustries="selectedIndustries"
            :otherIndustries="otherIndustries"
            :selectedGeographicExpertise="selectedGeographicExpertise"
            :expertiseLevel="profile.expertiseLevel"
            :ageExpertise="ageExpertise"
            :backgroundData="backgroundData"
            :additionalInfo="additionalInfo"
            :errors="formErrors"
            @toggle-brands="() => {}"
            @update-other-brand-experience="() => {}"
            @toggle-expertise-areas="updateSelectedExpertiseAreas"
            @update-other-expertise="updateOtherExpertise"
            @toggle-industries="updateSelectedIndustries"
            @update-other-industries="updateOtherIndustries"
            @toggle-geographic-expertise="updateSelectedGeographicExpertise"
            @update-expertise-level="updateExpertiseLevel"
            @rate-age-expertise="rateAgeExpertise"
            @rate-background="rateBackground"
            @update-additional-info="updateAdditionalInfo"
          />
          
          <!-- Additional validation error messages for expertise sections -->
          <div v-if="formErrors.expertiseAreas" class="error-message mt-2">{{ formErrors.expertiseAreas }}</div>
          <div v-if="formErrors.industries" class="error-message mt-2">{{ formErrors.industries }}</div>
          <div v-if="formErrors.geographicExpertise" class="error-message mt-2">{{ formErrors.geographicExpertise }}</div>
          <div v-if="formErrors.ageExpertise" class="error-message mt-2">{{ formErrors.ageExpertise }}</div>
          <div v-if="formErrors.backgroundExpertise" class="error-message mt-2">{{ formErrors.backgroundExpertise }}</div>
        </v-window-item>

        <!-- Password Tab -->
        <v-window-item :value="2">
          <!-- General error messages for password updates -->
          <div v-if="formErrors.password_change_error || formErrors.detail" class="error-message-section mb-4">
            <div class="error-message-box">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <span>{{ formErrors.password_change_error || formErrors.detail }}</span>
            </div>
          </div>
          
          <form-section title="Change Password">
            <v-row>
              <v-col cols="12" md="6">
                <base-input
                  id="currentPassword"
                  label="Current Password"
                  v-model="passwords.current"
                  placeholder="Enter your current password"
                  type="password"
                  required
                  :error="formErrors.old_password || formErrors.current_password"
                />
              </v-col>
              <v-col cols="12" md="6"></v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="newPassword"
                  label="New Password"
                  v-model="passwords.new"
                  placeholder="Enter your new password"
                  type="password"
                  required
                  :error="formErrors.new_password"
                />
              </v-col>
              <v-col cols="12" md="6">
                <base-input
                  id="confirmPassword"
                  label="Confirm New Password"
                  v-model="passwords.confirm"
                  placeholder="Confirm your new password"
                  type="password"
                  required
                  :error="formErrors.confirm_password"
                />
              </v-col>
            </v-row>
          </form-section>
        </v-window-item>

        <!-- Notifications Tab -->
        <v-window-item :value="3">
          <form-section title="Notification Preferences">
            <div class="switch-group">
              <div class="switch-option">
                <v-switch
                  v-model="notifications.newSurveys"
                  color="#e94057"
                  density="compact"
                  hide-details
                ></v-switch>
                <label>Notify me about new surveys</label>
              </div>
              
              <div class="switch-option">
                <v-switch
                  v-model="notifications.deadlineReminders"
                  color="#e94057"
                  density="compact"
                  hide-details
                ></v-switch>
                <label>Deadline reminders</label>
              </div>
              
              <div class="switch-option">
                <v-switch
                  v-model="notifications.payouts"
                  color="#e94057"
                  density="compact"
                  hide-details
                ></v-switch>
                <label>Payout notifications</label>
              </div>
            </div>
          </form-section>
        </v-window-item>
      </v-window>

      <div class="actions-container mt-6">
        <action-button 
          variant="secondary" 
          @click="resetForm"
          class="mr-4"
        >
          Cancel
        </action-button>
        <action-button
          variant="primary"
          @click="saveChanges"
          :loading="loading"
          right-icon="mdi-check"
        >
          Save Changes
        </action-button>
      </div>
    </v-container>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import apiService from '@/services/apiService';
import BaseInput from '@/components/common/BaseInput.vue';
import FormSection from '@/components/common/FormSection.vue';
import RadioGroup from '@/components/common/RadioGroup.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import ExpertiseForm from '@/components/common/ExpertiseForm.vue';
import { useToast } from 'vue-toastification';

export default {
  name: 'AccountView',
  components: {
    BaseInput,
    FormSection,
    RadioGroup,
    ActionButton,
    ExpertiseForm
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
      birthdateMenu: false,
      formErrors: {},
      
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        birthdate: null,
        gender: '',
        expertiseLevel: '',
        specialization: '',
        profilePictureUrl: '',
        linkedinProfileUrl: '',
        currentCompany: ''
      },
      
      // Profile picture handling
      profilePictureFile: null,
      profilePicturePreview: null,
      
      // Selected items - Multi-select arrays
      // selectedBrands: [],  // REMOVED - No longer collected
      // otherBrandExperience: '',  // REMOVED - No longer collected
      
      // Keep for backward compatibility but not used
      selectedBrands: [],
      otherBrandExperience: '',
      selectedExpertiseAreas: [],
      otherExpertise: '',
      selectedIndustries: [],
      otherIndustries: '',
      selectedGeographicExpertise: [],
      
      // Design expertise ratings
      designExpertise: {
        package: 0,
        product: 0,
        graphic: 0
      },
      
      // Age expertise ratings
      ageExpertise: {
        children: 0,
        teenagers: 0,
        adults: 0,
        seniors: 0
      },
      
      // Geographic expertise ratings
      geoExpertise: {
        northAmerica: 0,
        europe: 0,
        asiaPacific: 0,
        latinAmerica: 0,
        middleEastAfrica: 0
      },
      
      // Professional background ratings
      backgroundData: {
        agency: 0,
        design: 0,
        academia: 0,
        marketing: 0,
        marketResearch: 0
      },
      
      // Additional info
      additionalInfo: '',
      
      // Password change
      passwords: {
        current: '',
        new: '',
        confirm: ''
      },
      
      // Notification preferences
      notifications: {
        newSurveys: true,
        deadlineReminders: true,
        payouts: true
      },
      
      // Options
      genderOptions: [
        { text: 'Male', value: 'male' },
        { text: 'Female', value: 'female' },
        { text: 'Other', value: 'other' },
        { text: 'Prefer not to say', value: 'prefer_not_to_say' }
      ],
      expertiseLevelOptions: [
        { text: 'Junior', value: 'junior' },
        { text: 'Mid-level', value: 'mid' },
        { text: 'Senior', value: 'senior' }
      ]
    };
  },
  computed: {
    formattedBirthdate() {
      return this.profile.birthdate ? format(parseISO(this.profile.birthdate), 'MM/dd/yyyy') : '';
    },
    profileInitials() {
      const firstInitial = this.profile.firstName ? this.profile.firstName.charAt(0).toUpperCase() : '';
      const lastInitial = this.profile.lastName ? this.profile.lastName.charAt(0).toUpperCase() : '';
      return (firstInitial + lastInitial) || firstInitial || lastInitial || 'U';
    }
  },
  async created() {
    await this.fetchUserProfile();
    await this.fetchNotificationPreferences();
  },
  methods: {
    async fetchUserProfile() {
      try {
        this.loading = true;
        const response = await apiService.getExpertProfile();
        
        // Set basic profile data
        const { user, ...expertProfile } = response.data;
        this.profile.firstName = expertProfile.first_name;
        this.profile.lastName = expertProfile.last_name;
        this.profile.email = user.email;
        this.profile.mobilePhone = expertProfile.mobile_phone;
        this.profile.birthdate = expertProfile.birthdate;
        this.profile.gender = expertProfile.gender;
        this.profile.expertiseLevel = expertProfile.expertise_level;
        this.profile.specialization = expertProfile.specialization;
        this.profile.linkedinProfileUrl = expertProfile.linkedin_profile_url || '';
        this.profile.currentCompany = expertProfile.current_company || '';
        
        // Set profile picture URL
        this.profile.profilePictureUrl = expertProfile.photoUrl || '';
        
        // Set multi-select arrays and "other" text fields - using proper ID arrays
        // Use brand_ids field if available, otherwise fallback to converting brand names
        // COMMENTED OUT - Brand data no longer needed
        // this.selectedBrands = expertProfile.brand_ids || (expertProfile.brand_expertise || []).map(brand => {
        //   if (typeof brand === 'object' && brand.id) return brand.id;
        //   if (typeof brand === 'string') return this.convertBrandNameToId(brand);
        //   return brand;
        // }).filter(id => id !== null && id !== undefined);
        // 
        // this.otherBrandExperience = expertProfile.other_brand_experience || '';
        
        // Keep for backward compatibility but not used
        this.selectedBrands = [];
        this.otherBrandExperience = '';
        
        this.selectedExpertiseAreas = (expertProfile.expertise_areas || []).map(i => (typeof i === 'object' ? i.id : i));
        this.otherExpertise = expertProfile.other_expertise || '';
        this.selectedIndustries = (expertProfile.industries || []).map(i => (typeof i === 'object' ? i.id : i));
        this.otherIndustries = expertProfile.other_industries || '';
        this.selectedGeographicExpertise = (expertProfile.geographical_expertise || []).map(i => (typeof i === 'object' ? i.id : i));
        
        // Age expertise ratings
        this.ageExpertise = {
          children: expertProfile.age_expertise_children || 0,
          teenagers: expertProfile.age_expertise_teenagers || 0,
          adults: expertProfile.age_expertise_adults || 0,
          seniors: expertProfile.age_expertise_seniors || 0
        };
        
        // Professional background ratings - removed design_experience
        this.backgroundData = {
          agency: expertProfile.agency_experience || 0,
          academia: expertProfile.academia_experience || 0,
          marketing: expertProfile.marketing_experience || 0,
          marketResearch: expertProfile.market_research_experience || 0
        };
        
        // Additional info
        this.additionalInfo = expertProfile.additional_info || '';
        
      } catch (error) {
        console.error('Error fetching user profile:', error);
        this.toast.error('Failed to load profile data');
      } finally {
        this.loading = false;
      }
    },
    
    async fetchNotificationPreferences() {
      try {
        const response = await apiService.get('/auth/notification-preferences/');
        this.notifications = {
          newSurveys: response.data.newSurveys !== undefined ? response.data.newSurveys : true,
          deadlineReminders: response.data.deadlineReminders !== undefined ? response.data.deadlineReminders : true,
          payouts: response.data.payouts !== undefined ? response.data.payouts : true
        };
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        // Don't show error toast as this is not critical data
        // Use default values that are already set in data()
      }
    },
    
    async saveChanges() {
      try {
        this.loading = true;
        this.formErrors = {}; // Clear previous errors
        
        // Validate all required fields
        const errors = {};
        
        // Basic profile validation
        if (!this.profile.firstName || this.profile.firstName.trim() === '') {
          errors.first_name = 'First name is required';
        }
        if (!this.profile.lastName || this.profile.lastName.trim() === '') {
          errors.last_name = 'Last name is required';
        }
        if (!this.profile.email || this.profile.email.trim() === '') {
          errors.email = 'Email is required';
        }
        if (!this.profile.mobilePhone || this.profile.mobilePhone.trim() === '') {
          errors.mobile_phone = 'Mobile phone is required';
        }
        if (!this.profile.birthdate) {
          errors.birthdate = 'Birthdate is required';
        }
        if (!this.profile.gender) {
          errors.gender = 'Gender is required';
        }
        if (!this.profile.expertiseLevel) {
          errors.expertise_level = 'Expertise level is required';
        }
        if (!this.profile.currentCompany || this.profile.currentCompany.trim() === '') {
          errors.current_company = 'Company you work for is required';
        }
        
        // Brand experience validation - REMOVED (No longer required)
        // if (!this.selectedBrands || this.selectedBrands.length === 0) {
        //   errors.brands = 'Please select at least one TCCC brand you have worked with';
        // }
        
        // Expertise areas validation
        if (!this.selectedExpertiseAreas || this.selectedExpertiseAreas.length === 0) {
          errors.expertiseAreas = 'Please select at least one area of expertise';
        }
        
        // Industries validation
        if (!this.selectedIndustries || this.selectedIndustries.length === 0) {
          errors.industries = 'Please select at least one industry you have experience with';
        }
        
        // Geographic expertise validation
        if (!this.selectedGeographicExpertise || this.selectedGeographicExpertise.length === 0) {
          errors.geographicExpertise = 'Please select at least one geographic region where you have expertise';
        }
        
        // Age expertise validation - at least one age group must be rated
        const ageRatings = [
          this.ageExpertise.children,
          this.ageExpertise.teenagers,
          this.ageExpertise.adults,
          this.ageExpertise.seniors
        ];
        if (ageRatings.every(rating => rating === 0)) {
          errors.ageExpertise = 'Please rate your expertise with at least one age group';
        }
        
        // Professional background validation - at least one area must be rated
        const backgroundRatings = [
          this.backgroundData.agency,
          this.backgroundData.academia,
          this.backgroundData.marketing,
          this.backgroundData.marketResearch
        ];
        if (backgroundRatings.every(rating => rating === 0)) {
          errors.backgroundExpertise = 'Please rate your experience in at least one professional background area';
        }
        
        // If there are validation errors, show them and stop
        if (Object.keys(errors).length > 0) {
          this.formErrors = errors;
          this.toast.error('Please fill in all required fields');
          // NEW: Navigate user to the tab & field that contains the first error
          this.handleValidationErrors(errors);
          this.loading = false;
          return;
        }
        
        // Prepare FormData for file upload
        const formData = new FormData();
        
        // Add profile picture if a new one was selected
        if (this.profilePictureFile) {
          formData.append('profile_picture', this.profilePictureFile);
        } else if (this.profile.profilePictureUrl === '') {
          // User removed existing profile picture
          formData.append('profile_picture', '');
        }
        
        // Prepare data for API - with proper conversions like in ExpertProfileView
        const profileData = {
          first_name: this.profile.firstName,
          last_name: this.profile.lastName,
          mobile_phone: this.profile.mobilePhone,
          birthdate: this.profile.birthdate,
          gender: this.profile.gender,
          expertise_level: this.profile.expertiseLevel,
          linkedin_profile_url: this.profile.linkedinProfileUrl,
          current_company: this.profile.currentCompany,
          
          // Multi-select fields as arrays of IDs
          industries: this.selectedIndustries,
          expertise_areas: this.selectedExpertiseAreas,
          geographical_expertise: this.selectedGeographicExpertise,
          // brands: this.selectedBrands, // REMOVED - No longer collected
          
          // "Other" text fields
          other_expertise: this.otherExpertise,
          other_industries: this.otherIndustries,
          // other_brand_experience: this.otherBrandExperience,  // REMOVED - No longer collected
          
          // Age expertise ratings
          age_expertise_children: this.ageExpertise.children,
          age_expertise_teenagers: this.ageExpertise.teenagers,
          age_expertise_adults: this.ageExpertise.adults,
          age_expertise_seniors: this.ageExpertise.seniors,
          
          // Professional background ratings - removed design fields
          agency_experience: this.backgroundData.agency,
          academia_experience: this.backgroundData.academia,
          marketing_experience: this.backgroundData.marketing,
          market_research_experience: this.backgroundData.marketResearch,
          
          // Additional info
          additional_info: this.additionalInfo
        };
        
        // Add non-file data to FormData
        Object.keys(profileData).forEach(key => {
          const value = profileData[key];
          if (Array.isArray(value)) {
            value.forEach(item => formData.append(key, item));
          } else if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
        
        const response = await apiService.updateExpertProfile(formData);
        
        if (response.data) {
          this.toast.success('Profile updated successfully!');
          // Clear the file input and preview
          this.profilePictureFile = null;
          this.profilePicturePreview = null;
          // Optionally refresh the profile data
          await this.fetchUserProfile();
        }
        
      } catch (error) {
        console.error('Error updating profile:', error);
        
        if (error.response && error.response.data && error.response.data.errors) {
          // Handle validation errors from backend
          this.formErrors = error.response.data.errors;
          this.toast.error(error.response.data.message || 'Please correct the errors below');
          // NEW: Navigate user to the tab & field that contains the first error coming from backend
          this.handleValidationErrors(this.formErrors);
        } else {
          this.toast.error('Failed to update profile. Please try again.');
        }
      } finally {
        this.loading = false;
      }
    },

    // NEW: Helper to switch tab & scroll to the first invalid input
    handleValidationErrors(errors) {
      if (!errors || Object.keys(errors).length === 0) return;

      // Map each possible error key to its corresponding tab index
      const errorTabMap = {
        // Profile tab (0)
        first_name: 0,
        last_name: 0,
        email: 0,
        mobile_phone: 0,
        birthdate: 0,
        gender: 0,
        expertise_level: 0,
        profile_picture: 0,
        linkedin_profile_url: 0, // Added for new field
        current_company: 0,
        // Expertise tab (1)
        expertiseAreas: 1,
        industries: 1,
        geographicExpertise: 1,
        ageExpertise: 1,
        backgroundExpertise: 1,
        // Password tab (2)
        old_password: 2,
        current_password: 2,
        new_password: 2,
        confirm_password: 2
      };

      // Determine first error key & associated tab
      const firstErrorKey = Object.keys(errors)[0];
      const targetTab = errorTabMap[firstErrorKey] !== undefined ? errorTabMap[firstErrorKey] : 0;

      // Switch tab if needed
      if (this.activeTab !== targetTab) {
        this.activeTab = targetTab;
      }

      // After DOM updates, try to focus/scroll to the problem field
      this.$nextTick(() => {
        // Simple mapping from error key to selector of related element
        const selectorMap = {
          first_name: '#firstName',
          last_name: '#lastName',
          email: '#email',
          mobile_phone: '#mobilePhone',
          birthdate: '#birthdate',
          gender: 'input[name="gender"]',
          expertise_level: '#expertiseLevel',
          linkedin_profile_url: '#linkedinProfileUrl', // Added for new field
          current_company: '#currentCompany',
          // The following selectors are section fallbacks – they might not be focusable but allow scrolling
          expertiseAreas: '[data-section="expertiseAreas"]',
          industries: '[data-section="industries"]',
          geographicExpertise: '[data-section="geographicExpertise"]',
          ageExpertise: '[data-section="ageExpertise"]',
          backgroundExpertise: '[data-section="backgroundExpertise"]',
          old_password: '#currentPassword',
          current_password: '#currentPassword',
          new_password: '#newPassword',
          confirm_password: '#confirmPassword'
        };

        const selector = selectorMap[firstErrorKey];
        let targetEl = selector ? this.$el.querySelector(selector) : null;

        // Fallback – first visible .error-message inside current tab
        if (!targetEl) {
          const tabContent = this.$el.querySelectorAll('.v-window-item')[this.activeTab];
          if (tabContent) {
            targetEl = tabContent.querySelector('.error-message');
          }
        }

        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof targetEl.focus === 'function') targetEl.focus();
        }
      });
    },
    
    handleProfilePictureChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.toast.error('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        this.toast.error('File size must be less than 5MB');
        return;
      }
      
      this.profilePictureFile = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.profilePicturePreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      if (this.formErrors.profile_picture) {
        delete this.formErrors.profile_picture;
      }
    },
    
    removeProfilePicture() {
      this.profilePictureFile = null;
      this.profilePicturePreview = null;
      this.profile.profilePictureUrl = '';
      
      // Reset file input
      if (this.$refs.profilePictureInput) {
        this.$refs.profilePictureInput.value = '';
      }
    },
    
    resetForm() {
      this.fetchUserProfile();
      this.fetchNotificationPreferences();
      this.passwords.current = '';
      this.passwords.new = '';
      this.passwords.confirm = '';
      // Reset profile picture
      this.profilePictureFile = null;
      this.profilePicturePreview = null;
      // Reset multi-select arrays and "other" fields
      this.selectedBrands = [];
      this.otherBrandExperience = '';
      this.selectedExpertiseAreas = [];
      this.otherExpertise = '';
      this.selectedIndustries = [];
      this.otherIndustries = '';
      this.selectedGeographicExpertise = [];
      this.profilePicturePreview = null; // Reset preview
      this.profilePictureFile = null; // Reset file
    },
    
    convertBrandNamesToIds(brandNames) {
      return brandNames
        .map(name => this.convertBrandNameToId(name))
        .filter(id => id !== null && id !== undefined);
    },

    convertBrandNameToId(name) {
      if (!name) return null;
      const normalized = String(name).toLowerCase().replace(/[-\s]/g, '');
      const map = {
        'cocacola': 1,
        'sprite': 2,
        'fanta': 3,
        'powerade': 4,
        'minutemaid': 5,
        'dasani': 6,
        'ades': 7,
        'smartwater': 8,
        'topochico': 9,
        'costacoffee': 10,
        'honesttea': 11,
        'simply': 12
      };
      return map[normalized] || null;
    },
    // Deprecated mapping from ID to name, kept for compatibility if needed elsewhere
    convertBrandIdsToNames(brandIds) {
      const mapById = {
        1: 'Coca-Cola',
        2: 'Sprite',
        3: 'Fanta',
        4: 'Powerade',
        5: 'Minute Maid',
        6: 'Dasani',
        7: 'AdeS',
        8: 'Smartwater',
        9: 'Topo Chico',
        10: 'Costa Coffee',
        11: 'Honest Tea',
        12: 'Simply'
      };
      return brandIds.map(id => mapById[id]).filter(Boolean);
    },
    
    isSelected(selectedArray, id) {
      if (!selectedArray || !Array.isArray(selectedArray)) {
        return false;
      }
      // Handle both object arrays and ID arrays
      return selectedArray.some(item => 
        typeof item === 'object' ? item.id === id : item === id
      );
    },
    
    toggleSelection(selectedArray, id) {
      if (!selectedArray || !Array.isArray(selectedArray)) {
        return;
      }
      
      const index = selectedArray.findIndex(item => 
        typeof item === 'object' ? item.id === id : item === id
      );
      
      if (index === -1) {
        selectedArray.push(id);
      } else {
        selectedArray.splice(index, 1);
      }
    },
    
    toggleBrand(id) {
      const index = this.selectedBrands.findIndex(brand => brand === id);
      if (index === -1) {
        this.selectedBrands.push(id);
      } else {
        this.selectedBrands.splice(index, 1);
      }
    },
    
    toggleIndustry(id) {
      const index = this.selectedIndustries.findIndex(industry => industry === id);
      if (index === -1) {
        this.selectedIndustries.push(id);
      } else {
        this.selectedIndustries.splice(index, 1);
      }
    },
    
    updateSpecialization(value) {
      this.selectedSpecialization = value;
    },
    
    updateCustomSpecialization(value) {
      this.customSpecialization = value;
    },
    
    updateExpertiseLevel(level) {
      this.profile.expertiseLevel = level;
    },
    
    rateDesignExpertise(data) {
      this.designExpertise[data.area] = data.level;
    },
    
    rateAgeExpertise(data) {
      this.ageExpertise[data.group] = data.level;
    },
    
    rateGeoExpertise(data) {
      this.geoExpertise[data.region] = data.level;
    },
    
    rateBackground(data) {
      this.backgroundData[data.area] = data.level;
    },
    
    updateAdditionalInfo(value) {
      this.additionalInfo = value;
    },
    
    // New event handlers for multi-select components
    updateSelectedBrands(selectedItems) {
      // MultiSelectWithOther emits an array of selected items (objects with id/name)
      // Convert to array of IDs for consistency
      this.selectedBrands = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateOtherBrandExperience(value) {
      this.otherBrandExperience = value;
    },
    
    updateSelectedExpertiseAreas(selectedItems) {
      this.selectedExpertiseAreas = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateOtherExpertise(value) {
      this.otherExpertise = value;
    },
    
    updateSelectedIndustries(selectedItems) {
      this.selectedIndustries = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
    
    updateOtherIndustries(value) {
      this.otherIndustries = value;
    },
    
    updateSelectedGeographicExpertise(selectedItems) {
      this.selectedGeographicExpertise = selectedItems.map(item => 
        typeof item === 'object' ? item.id : item
      );
    },
  }
};
</script>

<style scoped>
.account-view {
  padding: 20px 0;
  max-width: 1200px;
  margin: 0 auto;
}

.page-title {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
}

.expertise-section {
  margin-bottom: 30px;
}

.expertise-subtitle {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 15px;
  color: #333;
}

.selection-container {
  display: flex;
  flex-wrap: wrap;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

textarea.form-control {
  min-height: 100px;
  resize: vertical;
}

.form-control:focus {
  outline: none;
  border-color: #e94057;
}

.form-control:disabled {
  background-color: #f9f9f9;
  cursor: not-allowed;
}

.switch-group {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.switch-option {
  display: flex;
  align-items: center;
}

.switch-option label {
  margin-bottom: 0;
  margin-left: 10px;
}

.actions-container {
  display: flex;
  justify-content: flex-end;
}

/* Error Message Styles */
.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-message::before {
  content: '⚠';
  font-size: 12px;
}

/* Error State Styles */
.form-control.error {
  border-color: #f44336 !important;
  box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.1);
}

/* General Error Message Section */
.error-message-section {
  margin-bottom: 20px;
}

.error-message-box {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  font-size: 14px;
}

.mb-4 {
  margin-bottom: 20px;
}

/* Profile Picture Styles */
.profile-picture-section {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
}

.profile-picture-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.current-picture {
  flex-shrink: 0;
}

.profile-picture-img {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.profile-picture-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #e94057; /* Match sidebar avatar color */
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 40px;
}

.profile-picture-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.form-helper-text {
  display: block;
  color: #666;
  font-size: 12px;
  margin-top: 8px;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .profile-picture-container {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .profile-picture-actions {
    align-items: center;
  }
}
</style> 